import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import { Button, Divider, Grid, Menu, Pagination } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import { exportSalesToPDF } from "../../utils/exportSales";
import LinearProgress from "@mui/material/LinearProgress";
import RefreshIcon from "@mui/icons-material/Refresh";

const extractAmountValue = (value, preferredCurrency) => {
  if (value == null) return 0;

  if (typeof value === "number" || typeof value === "string") {
    return value;
  }

  if (typeof value === "object") {
    const selectedValue =
      preferredCurrency && value[preferredCurrency] != null
        ? value[preferredCurrency]
        : Object.values(value)[0];

    if (selectedValue && typeof selectedValue === "object") {
      return (
        selectedValue.source ??
        selectedValue.parsedValue ??
        selectedValue.amount ??
        0
      );
    }

    return selectedValue ?? 0;
  }

  return 0;
};

const parseAmount = (value) => {
  if (value == null || value === "") return 0;
  if (typeof value === "number") return value;

  const parsed = parseFloat(String(value).replace(/[^0-9.-]/g, ""));
  return Number.isNaN(parsed) ? 0 : parsed;
};

const getAmount = (value, preferredCurrency) =>
  parseAmount(extractAmountValue(value, preferredCurrency));

const formatAmount = (value) => parseAmount(value).toFixed(2);

const getSaleReturnAmount = (saleReturn) => {
  const returnItemsAmount = (saleReturn?.return_items || []).reduce(
    (sum, item) => sum + parseAmount(item.refund_amount),
    0
  );

  return parseAmount(saleReturn?.total_refound_amount) || returnItemsAmount;
};

function Row({ row }) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const itemCount = row.sale_items?.length ?? 0;
  const currency = row.currency_details?.title ?? "";

  return (
    <>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{row.customer_details?.name || "-"}</TableCell>
        <TableCell>{row.sale_date || "-"}</TableCell>
        <TableCell>
          {row.amount != null && parseFloat(row.amount) > 0
            ? `${parseFloat(row.amount).toFixed(2)} ${currency}`
            : "-"}
        </TableCell>
        <TableCell>
          {formatAmount(row.report_total_amount)} {currency}
        </TableCell>
        <TableCell>
          {formatAmount(row.report_paid_amount)} {currency}
        </TableCell>
        <TableCell>
          {formatAmount(row.report_return_amount)} {currency}
        </TableCell>
        <TableCell>
          {formatAmount(row.report_remaining_amount)} {currency}
        </TableCell>
        <TableCell>{itemCount}</TableCell>
      </TableRow>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 1 }}>
              <Typography variant="h6" gutterBottom component="div">
                {t("ReportIndex.sales_collapse_title")}
              </Typography>
              <Table size="small" aria-label="sale-items">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {t("ReportIndex.sales_collapse_product")}
                    </TableCell>
                    <TableCell>{t("ReportIndex.sales_collapse_qty")}</TableCell>
                    <TableCell>
                      {t("ReportIndex.sales_collapse_price")}
                    </TableCell>
                    <TableCell>
                      {t("ReportIndex.sales_collapse_total")}
                    </TableCell>
                    <TableCell>
                      {t("ReportIndex.sales_collapse_unit")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.sale_items?.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell component="th" scope="row">
                        {item.product_details?.name}
                      </TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>{item.sale_price}</TableCell>
                      <TableCell>{item.quantity * item.sale_price}</TableCell>
                      <TableCell>{item.unit_details?.title}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

Row.propTypes = {
  row: PropTypes.shape({
    customer_details: PropTypes.shape({ name: PropTypes.string }),
    sale_date: PropTypes.string,
    discount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    total_amount: PropTypes.any,
    total_paid: PropTypes.any,
    total_remained_amount: PropTypes.any,
    report_total_amount: PropTypes.number,
    report_paid_amount: PropTypes.number,
    report_return_amount: PropTypes.number,
    report_remaining_amount: PropTypes.number,
    sale_items: PropTypes.array,
    currency_details: PropTypes.shape({ title: PropTypes.string }),
  }).isRequired,
};

export default function SaleTable({ selectedBranch = "" }) {
  const auth = useAuth();
  const token = auth?.user?.token;
  const { t } = useTranslation();
  const allCustomersOption = useMemo(
    () => ({ id: "", name: t("ReportIndex.all_customers", "All Customers") }),
    [t]
  );

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [selectedCustomerOption, setSelectedCustomerOption] = useState(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState(null);
  const customerSearchFilter = useMemo(() => {
    const trimmedSearch = customerSearchTerm.trim();

    if (!trimmedSearch || selectedCustomer) return "";

    if (
      trimmedSearch.toLowerCase() === allCustomersOption.name.toLowerCase()
    ) {
      return "";
    }

    return trimmedSearch;
  }, [allCustomersOption.name, customerSearchTerm, selectedCustomer]);

  const queryString = useMemo(() => {
    const params = [];
    if (startDate) params.push(`start_date=${startDate.format("YYYY-MM-DD")}`);
    if (endDate) params.push(`end_date=${endDate.format("YYYY-MM-DD")}`);
    if (selectedBranch) params.push(`shop=${selectedBranch}`);
    if (selectedCustomer) params.push(`customer=${selectedCustomer}`);
    if (customerSearchFilter) {
      params.push(`search=${encodeURIComponent(customerSearchFilter)}`);
    }
    if (rowsPerPage === -1) {
      params.push("page_size=all");
      params.push("page=1");
    } else {
      params.push(`page=${page}`);
      params.push(`page_size=${rowsPerPage}`);
    }
    return params.join("&");
  }, [
    customerSearchFilter,
    endDate,
    page,
    rowsPerPage,
    selectedCustomer,
    startDate,
    selectedBranch,
  ]);

  const {
    data: salesData,
    isLoading: salesLoading,
    isError: salesError,
    refetch,
  } = useFetchObjects(
    [
      "sales",
      queryString,
      selectedCustomer,
      customerSearchFilter,
      page,
      rowsPerPage,
      selectedBranch,
    ],
    `shop/sales/?${queryString}`,
    token
  );

  const saleReturnsQueryString = useMemo(() => {
    const params = ["page_size=all"];
    if (startDate) params.push(`start_date=${startDate.format("YYYY-MM-DD")}`);
    if (endDate) params.push(`end_date=${endDate.format("YYYY-MM-DD")}`);
    if (selectedBranch) params.push(`shop=${selectedBranch}`);
    if (selectedCustomer) params.push(`customer=${selectedCustomer}`);
    return params.join("&");
  }, [startDate, endDate, selectedBranch, selectedCustomer]);

  const {
    data: saleReturnsData,
    isLoading: saleReturnsLoading,
    isError: saleReturnsError,
  } = useFetchObjects(
    ["sale-returns-sales-report", saleReturnsQueryString, selectedCustomer, selectedBranch],
    `shop/sale-returns/?${saleReturnsQueryString}`,
    token
  );

  // Fetch customers for search field
  const {
    data: customersData,
    isLoading: customersLoading,
  } = useFetchObjects(
    ["customers", "sale-report-filter", customerSearchTerm],
    `shop/customers/?search=${encodeURIComponent(
      customerSearchTerm.trim()
    )}&page_size=20`,
    token
  );

  const baseCustomerOptions = Array.isArray(customersData)
    ? customersData
    : customersData?.results || [];
  const customerOptions = useMemo(() => {
    const options = [allCustomersOption, ...baseCustomerOptions];
    if (!selectedCustomerOption) return options;

    const alreadyIncluded = options.some(
      (customer) =>
        String(customer?.id ?? "") === String(selectedCustomerOption?.id ?? "")
    );

    return alreadyIncluded
      ? options
      : [selectedCustomerOption, ...options];
  }, [allCustomersOption, baseCustomerOptions, selectedCustomerOption]);

  useEffect(() => {
    refetch();
  }, [
    customerSearchFilter,
    endDate,
    page,
    refetch,
    rowsPerPage,
    selectedCustomer,
    selectedBranch,
    startDate,
  ]);

  const sales =
    salesData && salesData.results
      ? salesData.results
      : Array.isArray(salesData)
        ? salesData
        : [];
  const saleReturns =
    saleReturnsData && saleReturnsData.results
      ? saleReturnsData.results
      : Array.isArray(saleReturnsData)
        ? saleReturnsData
        : [];
  const totalCount = salesData && salesData.count ? salesData.count : sales.length;

  const saleReturnTotals = useMemo(() => {
    return saleReturns.reduce((acc, saleReturn) => {
      const saleId = saleReturn.sale ?? saleReturn.sale_details?.id;
      if (!saleId) return acc;

      acc[saleId] = (acc[saleId] || 0) + getSaleReturnAmount(saleReturn);
      return acc;
    }, {});
  }, [saleReturns]);

  const reportSales = useMemo(() => {
    return sales.map((sale) => {
      const currency = sale.currency_details?.title;
      const totalAmount = getAmount(sale.effective_total_amount ?? sale.total_amount, currency);
      const paidAmount = getAmount(
        sale.received_amount ?? sale.total_paid,
        currency
      );
      const returnAmount = saleReturnTotals[sale.id] || 0;
      const remainingAmount = Math.max(0, totalAmount - paidAmount - returnAmount);

      return {
        ...sale,
        report_total_amount: totalAmount,
        report_paid_amount: paidAmount,
        report_return_amount: returnAmount,
        report_remaining_amount: remainingAmount,
      };
    });
  }, [sales, saleReturnTotals]);

  // Calculate summary totals
  const summaryTotals = useMemo(() => {
    return reportSales.reduce((acc, sale) => {
      return {
        totalAmount: acc.totalAmount + sale.report_total_amount,
        totalPaid: acc.totalPaid + sale.report_paid_amount,
        totalReturn: acc.totalReturn + sale.report_return_amount,
        totalRemained: acc.totalRemained + sale.report_remaining_amount,
      };
    }, { totalAmount: 0, totalPaid: 0, totalReturn: 0, totalRemained: 0 });
  }, [reportSales]);

  const open = Boolean(anchorEl);
  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleExportClick = (type) => {
    const today = new Date().toISOString().slice(0, 10);
    const base = `sales_${today}`;

    if (type === "pdf") exportSalesToPDF(reportSales, `${base}.pdf`);
    // Add Excel or other export functionality here
    handleClose();
  };

  if (salesLoading || saleReturnsLoading)
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <LinearProgress sx={{ width: "90%" }} />
      </Box>
    );

  if (salesError || saleReturnsError)
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          textAlign: "center",
        }}
      >
        <Typography variant="h6" color="error">
          {t("ReportIndex.error_fetching_data")}{" "}
          <RefreshIcon sx={{ cursor: "pointer", verticalAlign: "middle" }} />
        </Typography>
      </Box>
    );

  return (
    <>
      <Box sx={{ flexGrow: 1, mb: 5, m: "15px" }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 2,
                  alignItems: { xs: "stretch", md: "center" },
                }}
              >
                <DatePicker
                  label={t("ReportIndex.datepicker_start")}
                  value={startDate}
                  onChange={setStartDate}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <DatePicker
                  label={t("ReportIndex.datepicker_end")}
                  value={endDate}
                  onChange={setEndDate}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <Autocomplete
                  fullWidth
                  options={customerOptions}
                  value={selectedCustomerOption}
                  inputValue={customerSearchTerm}
                  onChange={(event, newValue) => {
                    const nextOption =
                      newValue && String(newValue.id ?? "") === ""
                        ? allCustomersOption
                        : newValue || null;

                    setSelectedCustomerOption(nextOption);
                    setSelectedCustomer(
                      nextOption?.id ? String(nextOption.id) : ""
                    );
                    setCustomerSearchTerm(nextOption?.name || "");
                    setPage(1);
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === "input") {
                      setCustomerSearchTerm(newInputValue);
                      if (
                        selectedCustomer ||
                        selectedCustomerOption?.id === "" ||
                        (selectedCustomerOption?.name &&
                          selectedCustomerOption.name !== newInputValue)
                      ) {
                        setSelectedCustomer("");
                        setSelectedCustomerOption(null);
                        setPage(1);
                      }
                    }

                    if (reason === "clear") {
                      setCustomerSearchTerm("");
                      setSelectedCustomer("");
                      setSelectedCustomerOption(allCustomersOption);
                      setPage(1);
                    }
                  }}
                  loading={customersLoading}
                  getOptionLabel={(option) => option?.name || ""}
                  isOptionEqualToValue={(option, value) =>
                    String(option?.id ?? "") === String(value?.id ?? "")
                  }
                  clearOnBlur={false}
                  openOnFocus
                  autoHighlight
                  noOptionsText={t(
                    "sale.noCustomersFound",
                    "No customers found"
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("ReportIndex.customer_filter", "Customer")}
                    />
                  )}
                />
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    setStartDate(null);
                    setEndDate(null);
                    setSelectedCustomer("");
                    setCustomerSearchTerm("");
                    setSelectedCustomerOption(allCustomersOption);
                    setPage(1);
                  }}
                  sx={{
                    whiteSpace: "nowrap",
                    width: { xs: "100%", md: "auto" },
                    flexShrink: 0,
                    height: 55,
                  }}
                >
                  {t("ReportIndex.btn_clear")}
                </Button>
              </Box>
            </Grid>

            <Grid
              item
              xs={12}
              md={4}
              sx={{
                display: "flex",
                justifyContent: { xs: "flex-start", md: "flex-end" },
              }}
            >
              <FormControl
                sx={{ m: 1, minWidth: 200, width: { xs: "100%", md: "auto" } }}
              >
                <Box>
                  <Button
                    id="export-button"
                    aria-controls={open ? "export-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? "true" : undefined}
                    onClick={handleClick}
                    variant="outlined"
                    fullWidth
                    sx={{
                      whiteSpace: "nowrap",
                      width: { xs: "100%", md: "auto" },
                      flexShrink: 0,
                      height: 55,
                    }}
                  >
                    {t("ReportIndex.btn_export")}
                  </Button>
                  <Menu
                    id="export-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    MenuListProps={{ "aria-labelledby": "export-button" }}
                  >
                    <MenuItem onClick={() => handleExportClick("excel")}>
                      {t("ReportIndex.export_excel")}
                    </MenuItem>
                    <MenuItem onClick={() => handleExportClick("pdf")}>
                      {t("ReportIndex.export_pdf")}
                    </MenuItem>
                    <MenuItem onClick={() => handleExportClick("none")}>
                      {t("ReportIndex.export_none")}
                    </MenuItem>
                  </Menu>
                </Box>
              </FormControl>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, ml: 2 }}>
        <Typography>{t("reports.rowsPerPage")}</Typography>
        <FormControl size="small">
          <Select value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={30}>30</MenuItem>
            <MenuItem value={-1}>{t("reports.all")}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Divider />

      <TableContainer component={Paper}>
        <Table aria-label="sales table">
          <TableHead>
            <TableRow>
              <TableCell colSpan={9} style={{ textAlign: "center" }}>
                <Typography variant="h4">
                  {t("ReportIndex.sales_tableTitle")}
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>{t("ReportIndex.sales_table_customer")}</TableCell>
              <TableCell>{t("ReportIndex.sales_table_date")}</TableCell>
              <TableCell>{t("ReportIndex.sales_table_discount")}</TableCell>
              <TableCell>{t("ReportIndex.sales_table_totalAmount")}</TableCell>
              <TableCell>{t("ReportIndex.sales_table_totalPaid")}</TableCell>
              <TableCell>
                {t("ReportIndex.returnSale_table_refundTotal", "Return Amount")}
              </TableCell>
              <TableCell>
                {t("ReportIndex.sales_table_totalRemained")}
              </TableCell>
              <TableCell>{t("ReportIndex.sales_table_itemCount")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reportSales.map((sale) => (
              <Row key={sale.id} row={sale} />
            ))}
            {/* Summary Row */}
            <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
              <TableCell />
              <TableCell sx={{ fontWeight: 'bold' }}>{t('Summary')}</TableCell>
              <TableCell />
              <TableCell />
              <TableCell sx={{ fontWeight: 'bold' }}>
                {summaryTotals.totalAmount.toFixed(2)}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {summaryTotals.totalPaid.toFixed(2)}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {summaryTotals.totalReturn.toFixed(2)}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {summaryTotals.totalRemained.toFixed(2)}
              </TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {rowsPerPage !== -1 && (
        <Grid container justifyContent="center" alignItems="center" padding="10px">
          <Pagination
            count={Math.ceil(totalCount / rowsPerPage)}
            page={page}
            onChange={(event, newPage) => setPage(newPage)}
            variant="outlined"
            color="primary"
          />
        </Grid>
      )}
    </>
  );
}
